import { createClient } from '@supabase/supabase-js';

// Supabase configuration
// These are public keys - safe to expose in frontend
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://bhckvcwekztuesunsmiq.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Log warning if not configured
if (!supabaseAnonKey) {
  console.warn('[Supabase] VITE_SUPABASE_ANON_KEY not set. Auth will not work.');
}

// Maximum time to wait for Web Lock acquisition before failing open (ms)
const LOCK_TIMEOUT_MS = 3000;

// Fetch timeouts: auth endpoints get a short timeout (token refresh should be fast),
// all other endpoints get a generous timeout for data queries.
const AUTH_FETCH_TIMEOUT_MS = 8000;
const DATA_FETCH_TIMEOUT_MS = 15000;

// SDK storage key (must match authClient storageKey)
const SDK_SESSION_KEY = 'filmple-auth';

/**
 * Read the Supabase SDK session directly from localStorage.
 * Sync, never throws. SSR-safe (returns null when window is undefined).
 * Validates that access_token, refresh_token, and expires_at fields exist.
 */
export function getStoredSession(): {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user?: { id: string };
} | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SDK_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed.access_token !== 'string' || !parsed.access_token ||
      typeof parsed.refresh_token !== 'string' || !parsed.refresh_token ||
      typeof parsed.expires_at !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Custom fetch wrapper that enforces timeouts on all Supabase SDK HTTP requests.
 * Auth endpoints (/auth/v1/) get AUTH_FETCH_TIMEOUT_MS; others get DATA_FETCH_TIMEOUT_MS.
 * Properly chains with any caller-provided AbortSignal.
 */
function createTimeoutFetch(): typeof fetch {
  return (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const isAuthRequest = url.includes('/auth/v1/');
    const timeoutMs = isAuthRequest ? AUTH_FETCH_TIMEOUT_MS : DATA_FETCH_TIMEOUT_MS;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const onCallerAbort = () => controller.abort();
    if (init?.signal) {
      if (init.signal.aborted) controller.abort();
      else init.signal.addEventListener('abort', onCallerAbort, { once: true });
    }

    return fetch(input, { ...init, signal: controller.signal })
      .finally(() => {
        clearTimeout(timeoutId);
        init?.signal?.removeEventListener('abort', onCallerAbort);
      });
  };
}

/**
 * Fail-open auth lock: prevents Supabase auth operations from hanging indefinitely.
 *
 * GoTrueClient internally calls _acquireLock(-1, fn) for every auth operation
 * (getSession, signIn, signOut, token refresh). acquireTimeout=-1 means "wait
 * forever". If a lock/init path wedges (cross-tab contention, storage stall),
 * getSession() hangs with no network activity.
 *
 * This custom lock caps the wait at LOCK_TIMEOUT_MS. On timeout it runs fn()
 * anyway (fail-open), trading strict mutual exclusion for availability.
 * If fn() was already started inside the lock callback, the error is propagated
 * (fnStarted guard prevents double-execution).
 *
 * fn() execution is NOT capped here — the per-request fetch timeout
 * (AUTH_FETCH_TIMEOUT_MS / DATA_FETCH_TIMEOUT_MS) is the sole timeout control.
 * The SDK's internal retry loop resolves naturally.
 */
async function failOpenLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  // If Web Locks API is not available, run fn() directly (SSR, old browsers)
  if (typeof globalThis === 'undefined' || !globalThis.navigator?.locks) {
    return fn();
  }

  // Use the SDK's timeout if positive and shorter than our cap; otherwise use our cap
  const effectiveTimeout =
    acquireTimeout > 0
      ? Math.min(acquireTimeout, LOCK_TIMEOUT_MS)
      : LOCK_TIMEOUT_MS;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);
  let fnStarted = false;

  try {
    return await globalThis.navigator.locks.request(
      name,
      { mode: 'exclusive', signal: controller.signal },
      async () => {
        clearTimeout(timeoutId);
        fnStarted = true;
        return fn();
      },
    );
  } catch (err: unknown) {
    clearTimeout(timeoutId);

    // If fn() was already running inside the lock callback, don't re-run — propagate the error
    if (fnStarted) throw err;

    // AbortError = timed out waiting for lock — fail open and run fn() anyway
    if (err instanceof DOMException && err.name === 'AbortError') {
      console.warn(
        `[Supabase] Auth lock "${name}" timed out after ${effectiveTimeout}ms — failing open`,
      );
      return fn();
    }

    // Other errors (InvalidStateError, etc.): also fail open
    console.warn(`[Supabase] Auth lock "${name}" error — failing open:`, err);
    return fn();
  }
}

// --- Auth Client: handles signIn, signOut, onAuthStateChange, refreshSession ---
// Uses SDK's built-in lock + autoRefreshToken. If it blocks on refresh,
// nothing critical depends on it synchronously.
export const authClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'filmple-auth',
    lock: failOpenLock,
    ...(import.meta.env.DEV ? { debug: true } : {}),
  },
  global: {
    fetch: createTimeoutFetch(),
  },
});

// --- Data Client: uses accessToken option, NEVER calls getSession(), NEVER blocks ---
// Every supabase.from() and supabase.channel() call proceeds instantly.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  accessToken: async () => {
    const s = getStoredSession();
    if (!s?.access_token) return supabaseAnonKey;
    const expiresInMs = (s.expires_at ?? 0) * 1000 - Date.now();
    if (expiresInMs < 0) {
      // Token is expired — trigger refresh, don't send stale JWT
      authClient.auth.refreshSession().catch(() => {});
      return supabaseAnonKey;
    } else if (expiresInMs < 60_000) {
      // Token near expiry — trigger proactive refresh, still send current token
      authClient.auth.refreshSession().catch(() => {});
    }
    return s.access_token;
  },
  global: {
    fetch: createTimeoutFetch(),
  },
});

// --- Proactive refresh interval (30s) ---
// Checks token expiry and triggers refresh on authClient if within 60s of expiry.
// Fire-and-forget. Runs in browser only.
let refreshIntervalId: ReturnType<typeof setInterval> | undefined;

if (typeof window !== 'undefined') {
  if (refreshIntervalId) clearInterval(refreshIntervalId);
  refreshIntervalId = setInterval(() => {
    const s = getStoredSession();
    if (!s?.expires_at) return;
    const expiresInMs = s.expires_at * 1000 - Date.now();
    if (expiresInMs < 60_000) {
      authClient.auth.refreshSession().catch(() => {});
    }
  }, 30_000);
}

// Clean up interval on Vite HMR
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (refreshIntervalId) clearInterval(refreshIntervalId);
  });
}

// Type exports for convenience
export type { User, Session } from '@supabase/supabase-js';
